/**
 * Multi-team switcher in the top bar (only when JWT team_ids.length > 1).
 * Remints the member JWT via AuthContext.switchTeam and drops project pick.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ShellMenu } from './ui/ShellMenu';

/**
 * Multi-team chrome menu (hidden for visitors or a single membership).
 * On change remints JWT via switchTeam and invalidates React Query caches.
 */
export function TeamSwitcher() {
  const { t } = useTranslation();
  const { user, switchTeam, isLoading, isVisitor } = useAuth();
  const queryClient = useQueryClient();
  const teamIds = user?.team_ids || [];

  // Visitors are scoped to a single team and cannot remint membership JWTs.
  if (isVisitor || teamIds.length <= 1) return null;

  const onSelect = async (teamId: string) => {
    const ok = await switchTeam(teamId);
    if (ok) {
      // Factory catalog + boards are team-scoped.
      void queryClient.invalidateQueries();
    }
  };

  const active = user?.team_id || teamIds[0] || '';

  return (
    <ShellMenu
      data-testid="team-switcher"
      ariaLabel={t('shell.teamSwitcherAria')}
      triggerLabel={active}
      disabled={isLoading}
      items={teamIds.map((id) => ({
        id,
        label: id,
        selected: id === active,
      }))}
      onSelect={(id) => void onSelect(id)}
    />
  );
}
